import React, { forwardRef } from "react";
import DatePicker, { ReactDatePickerCustomHeaderProps } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { CustomDropdown, DropdownOption } from "./CustomDropdown";
import "./DateSelector.css";

// Month and Year options
const MONTHS = [
  { value: 0, label: "January" },
  { value: 1, label: "February" },
  { value: 2, label: "March" },
  { value: 3, label: "April" },
  { value: 4, label: "May" },
  { value: 5, label: "June" },
  { value: 6, label: "July" },
  { value: 7, label: "August" },
  { value: 8, label: "September" },
  { value: 9, label: "October" },
  { value: 10, label: "November" },
  { value: 11, label: "December" },
];

const getYearOptions = (minYear?: number, maxYear?: number) => {
  const currentYear = new Date().getFullYear();
  const startYear = minYear || currentYear - 100;
  const endYear = maxYear || currentYear;
  const years = [];
  for (let year = endYear; year >= startYear; year--) {
    years.push({ value: year, label: year.toString() });
  }
  return years;
};

// Custom input component for DatePicker
interface CustomDateInputProps {
  value?: string;
  onClick?: () => void;
  placeholder?: string;
  className?: string;
}

const CustomDateInput = forwardRef<HTMLButtonElement, CustomDateInputProps>(
  ({ value, onClick, placeholder = "Select date", className = "" }, ref) => {
    return (
      <button
        type="button"
        className={`date-selector-button ${className}`}
        onClick={onClick}
        ref={ref}
      >
        <CalendarIcon className="date-selector-icon" />
        <span>{value || placeholder}</span>
      </button>
    );
  }
);
CustomDateInput.displayName = "CustomDateInput";

// Custom header component with native select
interface CustomDatePickerHeaderProps extends ReactDatePickerCustomHeaderProps {
  minDate?: Date;
  maxDate?: Date;
}

const CustomDatePickerHeader: React.FC<CustomDatePickerHeaderProps> = ({
  date,
  changeMonth,
  changeYear,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  minDate,
  maxDate,
}) => {
  const currentMonth = date.getMonth();
  const currentYear = date.getFullYear();

  const minYear = minDate ? minDate.getFullYear() : undefined;
  const maxYear = maxDate ? maxDate.getFullYear() : undefined;
  const yearOptions = getYearOptions(minYear, maxYear);

  const handleMonthChange = (value: number | string) => {
    const newMonth = typeof value === 'number' ? value : parseInt(String(value), 10);
    changeMonth(newMonth);
  };

  const handleYearChange = (value: number | string) => {
    const newYear = typeof value === 'number' ? value : parseInt(String(value), 10);
    changeYear(newYear);
  };

  const monthOptions: DropdownOption[] = MONTHS.map((month) => ({
    value: month.value,
    label: month.label,
  }));

  const yearDropdownOptions: DropdownOption[] = yearOptions.map((year) => ({
    value: year.value,
    label: year.label,
  }));

  return (
    <div className="date-selector-custom-header">
      <div className="date-selector-dropdowns">
        <button
          type="button"
          className="date-selector-nav-button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          aria-label="Previous month"
        >
          <ChevronLeft size={18} />
        </button>

        <CustomDropdown
          options={monthOptions}
          value={currentMonth}
          onChange={handleMonthChange}
          className="date-selector-month-dropdown"
          ariaLabel="Select month"
        />

        <CustomDropdown
          options={yearDropdownOptions}
          value={currentYear}
          onChange={handleYearChange}
          className="date-selector-year-dropdown"
          ariaLabel="Select year"
        />

        <button
          type="button"
          className="date-selector-nav-button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          aria-label="Next month"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

// DateSelector component props
export interface DateSelectorProps {
  selected?: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  className?: string;
  customInputClassName?: string;
}

export const DateSelector: React.FC<DateSelectorProps> = ({
  selected,
  onChange,
  placeholder = "Select date",
  dateFormat = "dd MMM yyyy",
  minDate,
  maxDate,
  disabled = false,
  className = "",
  customInputClassName = "",
}) => {
  return (
    <div className="date-selector-wrapper">
      <DatePicker
        selected={selected}
        onChange={onChange}
        dateFormat={dateFormat}
        placeholderText={placeholder}
        customInput={
          <CustomDateInput
            placeholder={placeholder}
            className={customInputClassName}
          />
        }
        formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3)}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        className={className}
        renderCustomHeader={(props) => (
          <CustomDatePickerHeader
            {...props}
            minDate={minDate}
            maxDate={maxDate}
          />
        )}
      />
    </div>
  );
};

export default DateSelector;
